import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type Editor, EditorContent } from '@tiptap/react';
import { cn } from '@shadcn/lib/utils';
import { RichTextToolbar } from 'components/richText';
import { useDropdownFit } from 'hooks/useDropdownFit';
import { useRichTextField } from 'hooks/useRichTextField';
import { useTextBuildingBlocks } from 'hooks/useTextBuildingBlocks';
import { toBuildingBlockDoc } from './placeholders/buildingBlockContent';
import { getAnchorRect } from './placeholders/caretAnchor';
import { placeholderExtensions } from './placeholders/placeholderNodes';
import {
  TemplateSuggestion,
  type TemplateSuggestionBridge,
} from './placeholders/templateSuggestion';
import { usePlaceholderNavigation } from './placeholders/usePlaceholderNavigation';
import { useSelectionListResolution } from './placeholders/useSelectionListResolution';
import { useTextBuildingBlockAutocomplete } from './placeholders/useTextBuildingBlockAutocomplete';
import { SelectionListSelect } from './SelectionListSelect';
import { TextBuildingBlockDropdown } from './TextBuildingBlockDropdown';
import {
  Field,
  FieldHelper,
  FieldLabel,
  FieldToolbarRow,
  TEXT_BUILDING_BLOCK_FIELD_CLASS,
} from './TextBuildingBlockTextarea.styles';

interface TextBuildingBlockTextareaProps {
  section: string;
  label: string;
  helper?: string;
  value: string;
  onValueChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  className?: string;
  renderActions?: (editor: Editor) => ReactNode;
}

// Inner edges of the nearest horizontally-clipping ancestor (or the document). clientWidth excludes
// the scrollbar, so clamping the dropdown to this can't itself force a horizontal scrollbar.
function horizontalClipBounds(el: HTMLElement | null): { left: number; right: number } {
  for (let node = el?.parentElement ?? null; node; node = node.parentElement) {
    if (/(auto|scroll|hidden|clip)/.test(getComputedStyle(node).overflowX)) {
      const left = node.getBoundingClientRect().left + node.clientLeft;
      return { left, right: left + node.clientWidth };
    }
  }
  return { left: 0, right: document.documentElement.clientWidth };
}

export const TextBuildingBlockTextarea = ({
  section,
  label,
  helper,
  value,
  onValueChange,
  id,
  placeholder,
  className,
  renderActions,
}: TextBuildingBlockTextareaProps) => {
  const labelId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const blurTimer = useRef<number | null>(null);
  const [isFocused, setIsFocused] = useState(false);
  const { data, isError } = useTextBuildingBlocks();
  const selectionLists = useMemo(
    () => (data ?? []).flatMap((block) => block.selectionLists),
    [data]
  );

  const attributes = useMemo(
    () => ({
      ...(id ? { id } : {}),
      'aria-labelledby': labelId,
      role: 'textbox',
      'aria-multiline': 'true',
      class: cn(TEXT_BUILDING_BLOCK_FIELD_CLASS, className),
    }),
    [id, labelId, className]
  );

  const navigationRef = useRef<{
    selectList: (pos: number) => void;
    advance: (fromPos: number) => boolean;
    retreat: (fromPos: number) => boolean;
  } | null>(null);
  const suggestionBridgeRef = useRef<TemplateSuggestionBridge | null>(null);
  const activationPosRef = useRef<number | null>(null);
  const extraExtensions = useMemo(
    () => [
      ...placeholderExtensions({
        onActivateList: (pos) => navigationRef.current?.selectList(pos),
        onAdvance: (fromPos) => navigationRef.current?.advance(fromPos) ?? false,
        onRetreat: (fromPos) => navigationRef.current?.retreat(fromPos) ?? false,
        consumeActivation: (pos) => {
          if (activationPosRef.current !== pos) return false;
          activationPosRef.current = null;
          return true;
        },
      }),
      TemplateSuggestion.configure({ getBridge: () => suggestionBridgeRef.current }),
    ],
    []
  );
  const parseValue = useCallback((html: string) => toBuildingBlockDoc(html), []);

  const { editor } = useRichTextField({
    value,
    onChange: onValueChange,
    placeholder,
    attributes,
    extraExtensions,
    parseValue,
    onKeyDown: (event) => {
      if (resolution.isOpen && resolution.onKeyDown(event)) return true;
      return navigation.onKeyDown(event);
    },
  });

  const navigation = usePlaceholderNavigation({
    editor,
    onActivate: (pos) => {
      activationPosRef.current = pos;
    },
  });
  const resolution = useSelectionListResolution({
    editor,
    activeListPos: navigation.activeListPos,
    selectionLists,
    onResolved: navigation.goToNextFrom,
  });
  const onInserted = useCallback(
    (fromPos: number) => navigation.goToNextFrom(fromPos),
    [navigation]
  );
  const autocomplete = useTextBuildingBlockAutocomplete({
    section,
    editor,
    textBuildingBlocks: data ?? [],
    loadError: isError,
    onInserted,
  });

  useEffect(() => {
    navigationRef.current = {
      selectList: (pos: number) => editor.chain().focus().setNodeSelection(pos).run(),
      advance: (fromPos: number) => navigation.goToNextFrom(fromPos),
      retreat: (fromPos: number) => navigation.goToPrevFrom(fromPos),
    };
  }, [editor, navigation]);

  useEffect(() => {
    suggestionBridgeRef.current = autocomplete.bridge;
  }, [autocomplete.bridge]);

  // Track focus at the container: a slot's <input> blurs the editable but stays inside the field.
  const closeAutocomplete = autocomplete.close;
  const deselectPlaceholder = navigation.deselect;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const onFocusIn = () => {
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
      setIsFocused(true);
    };
    const onFocusOut = (event: FocusEvent) => {
      if (container.contains(event.relatedTarget as Node | null)) return;
      setIsFocused(false);
      deselectPlaceholder();
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
      blurTimer.current = window.setTimeout(() => closeAutocomplete(), 0);
    };
    container.addEventListener('focusin', onFocusIn);
    container.addEventListener('focusout', onFocusOut);
    return () => {
      container.removeEventListener('focusin', onFocusIn);
      container.removeEventListener('focusout', onFocusOut);
    };
  }, [closeAutocomplete, deselectPlaceholder]);

  const { isOpen: autocompleteIsOpen, getAnchorRect: getAutocompleteAnchorRect } = autocomplete;
  const getAnchorBottom = useCallback(() => {
    if (resolution.isOpen && resolution.anchorPos !== null) {
      try {
        return editor.view.coordsAtPos(resolution.anchorPos).bottom;
      } catch {
        return null;
      }
    }
    if (autocompleteIsOpen) return getAutocompleteAnchorRect()?.bottom ?? null;
    return null;
  }, [
    editor,
    resolution.isOpen,
    resolution.anchorPos,
    autocompleteIsOpen,
    getAutocompleteAnchorRect,
  ]);
  const getScrollTarget = useCallback(() => containerRef.current, []);
  const dropdownAnchorKey = resolution.isOpen
    ? `list:${resolution.anchorPos}`
    : autocompleteIsOpen
      ? 'autocomplete'
      : null;
  const dropdownMaxHeight = useDropdownFit(
    getAnchorBottom,
    resolution.isOpen || autocompleteIsOpen,
    getScrollTarget,
    dropdownAnchorKey
  );

  const containerRect = containerRef.current?.getBoundingClientRect() ?? null;
  const autocompleteRect = autocompleteIsOpen ? getAutocompleteAnchorRect() : null;
  const autocompleteAnchor =
    autocompleteRect && containerRect
      ? {
          top: autocompleteRect.bottom - containerRect.top,
          left: autocompleteRect.left - containerRect.left,
        }
      : null;
  const resolutionAnchor =
    resolution.isOpen && resolution.anchorPos !== null
      ? getAnchorRect(editor, resolution.anchorPos, containerRef.current)
      : null;

  // Slide the caret-pinned dropdown left when it would overflow the right clip edge. Retrigger only
  // on layout-independent signals (caret document position, item count) and read the caret through a
  // ref, so the shift can't perturb its own inputs and loop.
  const caretViewportLeft =
    autocompleteRect?.left ??
    (resolutionAnchor && containerRect ? containerRect.left + resolutionAnchor.left : null);
  const caretViewportLeftRef = useRef(caretViewportLeft);
  caretViewportLeftRef.current = caretViewportLeft;
  const cursorPos = resolution.isOpen || autocompleteIsOpen ? editor.state.selection.from : -1;
  const itemCount = autocomplete.items.length;
  const [dropdownLeftShift, setDropdownLeftShift] = useState(0);
  useLayoutEffect(() => {
    const caretLeft = caretViewportLeftRef.current;
    const dropdown = containerRef.current?.querySelector<HTMLElement>('[role="listbox"]');
    if (caretLeft === null || !dropdown) {
      setDropdownLeftShift(0);
      return;
    }
    const margin = 8;
    const { left: clipLeft, right: clipRight } = horizontalClipBounds(containerRef.current);
    const maxLeft = clipRight - margin - dropdown.offsetWidth;
    const target = Math.max(clipLeft + margin, Math.min(caretLeft, maxLeft));
    setDropdownLeftShift(target - caretLeft);
  }, [dropdownAnchorKey, cursorPos, itemCount]);
  const shiftedAutocompleteAnchor = autocompleteAnchor
    ? { ...autocompleteAnchor, left: autocompleteAnchor.left + dropdownLeftShift }
    : null;
  const shiftedResolutionAnchor = resolutionAnchor
    ? { ...resolutionAnchor, left: resolutionAnchor.left + dropdownLeftShift }
    : null;

  useEffect(
    () => () => {
      if (blurTimer.current !== null) window.clearTimeout(blurTimer.current);
    },
    []
  );

  return (
    <Field>
      <FieldLabel id={labelId} htmlFor={id}>
        {label}
      </FieldLabel>
      <FieldToolbarRow>
        {helper ? <FieldHelper>{helper}</FieldHelper> : null}
        <RichTextToolbar
          editor={editor}
          label={label}
          disabled={navigation.isPlaceholderActive || autocomplete.isOpen}
        />
        {renderActions?.(editor)}
      </FieldToolbarRow>
      <div className="relative" ref={containerRef}>
        <EditorContent editor={editor} />
        {isFocused && resolution.isOpen ? (
          <SelectionListSelect
            name={resolution.name}
            options={resolution.options}
            multiSelect={resolution.multiSelect}
            available={resolution.available}
            selected={resolution.selected}
            activeIndex={resolution.activeIndex}
            maxHeight={dropdownMaxHeight}
            position={shiftedResolutionAnchor ?? undefined}
            onOptionClick={resolution.onOptionClick}
            onNoSelection={resolution.onNoSelection}
            onCommit={resolution.onCommit}
            onHover={resolution.onHover}
          />
        ) : isFocused && autocomplete.isOpen ? (
          <TextBuildingBlockDropdown
            items={autocomplete.items}
            activeIndex={autocomplete.activeIndex}
            message={autocomplete.message}
            maxHeight={dropdownMaxHeight}
            position={shiftedAutocompleteAnchor ?? undefined}
            onSelect={autocomplete.select}
            onHover={autocomplete.setActiveIndex}
          />
        ) : null}
      </div>
    </Field>
  );
};
